import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { publicStatistics, publicStatisticsOverview, publicStatisticsSources } from "../lib/api";
import { ExternalLink, ChevronDown, Info } from "lucide-react";

const COUNTRY_CODE_MAP = { DE: "DE", AT: "AT", CH: "CH" };

function buildStructuredData(stats, sources) {
  // Deterministische Reihenfolge → stabile Server-Render-Ausgabe.
  const latest = stats
    .map(s => s.publication_date)
    .filter(Boolean).sort().slice(-1)[0] || new Date().toISOString().slice(0, 10);

  const collection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "SAT DACH-Gesellschaftsbarometer",
    description: "Aktuelle amtliche Zahlen zu Ehe, Scheidung, Einpersonenhaushalten und Familie in Deutschland, Österreich und der Schweiz.",
    url: "https://sat-akademie.com/gesellschaftsbarometer",
    inLanguage: "de",
    dateModified: latest,
    publisher: {
      "@type": "Organization",
      name: "SAT · Schweizer Akademie für Transformation",
      url: "https://sat-akademie.com",
    },
    isBasedOn: sources.slice(0, 6).map(s => ({
      "@type": "CreativeWork",
      name: s.source_title,
      author: { "@type": "GovernmentOrganization", name: s.source_agency },
      url: s.source_url,
    })),
  };

  const datasets = stats.map(s => ({
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `${s.label} · ${COUNTRY_CODE_MAP[s.country]}`,
    description: s.definition,
    url: `https://sat-akademie.com/gesellschaftsbarometer?land=${s.country}`,
    identifier: s.id,
    keywords: [s.topic, s.country, s.indicator_key],
    inLanguage: "de",
    isAccessibleForFree: true,
    dateModified: s.publication_date,
    temporalCoverage: s.reference_period,
    spatialCoverage: {
      "@type": "Place",
      name: { DE: "Deutschland", AT: "Österreich", CH: "Schweiz" }[s.country],
    },
    measurementTechnique: s.caveat || s.definition,
    citation: `${s.source_agency}. ${s.source_title}. ${s.source_url}`,
    creator: { "@type": "GovernmentOrganization", name: s.source_agency },
    license: s.source_url,
    variableMeasured: [{ "@type": "PropertyValue", name: s.label, unitText: s.unit, value: s.value_numeric }],
  }));

  return { collection, datasets };
}

const COUNTRY_LABEL = { DE: "Deutschland", AT: "Österreich", CH: "Schweiz" };
const TOPIC_LABEL = {
  marriage: "Ehe", divorce: "Scheidung",
  households: "Haushalte", family_children: "Familie & Kinder",
};
const STATUS_LABEL = { definitive: "definitiv", provisional: "provisorisch" };

const fmt = (n, unit) => {
  if (n == null) return "—";
  if (unit === "count") return new Intl.NumberFormat("de-DE").format(n);
  if (unit === "children_per_woman")
    return new Intl.NumberFormat("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  return new Intl.NumberFormat("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n) + " %";
};

function StatisticCard({ s }) {
  return (
    <article className="border border-white/10 rounded-lg p-5 hover:border-[#D4AF37]/40 transition-colors"
             data-testid={`stat-card-${s.country}-${s.indicator_key}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70">
          {COUNTRY_LABEL[s.country]} · {TOPIC_LABEL[s.topic]}
        </div>
        <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${
          s.publication_status === "definitive"
            ? "text-emerald-300 border-emerald-400/30"
            : "text-amber-300 border-amber-400/30"
        }`}>
          {STATUS_LABEL[s.publication_status]}
        </span>
      </div>
      <div className="text-4xl font-serif text-white mb-1"
           aria-label={`${fmt(s.value_numeric, s.unit)}, ${s.label}, ${COUNTRY_LABEL[s.country]}, ${s.reference_period}`}>
        {fmt(s.value_numeric, s.unit)}
      </div>
      <div className="text-sm text-white/70 mb-3">{s.label} · {s.reference_period}</div>
      <p className="text-xs text-white/60 leading-relaxed mb-2">{s.definition}</p>
      {s.caveat && (
        <p className="text-xs text-white/45 leading-relaxed mb-3 flex gap-1.5">
          <Info size={12} className="flex-shrink-0 mt-[2px] text-[#D4AF37]/60" />
          <span>{s.caveat}</span>
        </p>
      )}
      <a href={s.source_url} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline"
         data-testid={`source-link-${s.country}-${s.indicator_key}`}>
        {s.source_agency} <ExternalLink size={11} />
      </a>
      <div className="text-[10px] text-white/30 mt-2">Quelle geprüft: {s.source_checked_at}</div>
    </article>
  );
}

function BarChart({ title, data, unit, ariaId }) {
  const max = Math.max(...data.map(d => d.value)) || 1;
  return (
    <figure className="border border-white/10 rounded-lg p-5" role="group" aria-labelledby={ariaId}
            data-testid={`chart-${ariaId}`}>
      <figcaption id={ariaId} className="text-sm text-white/70 mb-4">{title}</figcaption>
      <div className="space-y-3">
        {data.map(d => (
          <div key={d.country}>
            <div className="flex items-center justify-between text-xs text-white/60 mb-1">
              <span>{COUNTRY_LABEL[d.country]} · {d.reference_period}</span>
              <span className="text-white/90 font-medium">{fmt(d.value, unit)}</span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#D4AF37]/80 to-[#D4AF37]/40"
                   style={{ width: `${(d.value / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
      <details className="mt-4 text-xs text-white/50">
        <summary className="cursor-pointer hover:text-white/70">Datentabelle anzeigen</summary>
        <table className="mt-2 w-full text-xs">
          <thead className="text-white/40">
            <tr><th className="text-left py-1">Land</th><th className="text-left py-1">Wert</th><th className="text-left py-1">Referenz</th></tr>
          </thead>
          <tbody>
            {data.map(d => (
              <tr key={d.country}><td className="py-1">{COUNTRY_LABEL[d.country]}</td><td>{fmt(d.value, unit)}</td><td>{d.reference_period}</td></tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

function SourcesAccordion({ sources }) {
  const byCountry = sources.reduce((acc, s) => {
    (acc[s.country] = acc[s.country] || []).push(s); return acc;
  }, {});
  return (
    <div className="space-y-3" data-testid="sources-accordion">
      {["DE", "AT", "CH"].map(c => (
        <details key={c} className="border border-white/10 rounded-lg p-4">
          <summary className="cursor-pointer text-white/80 font-serif text-lg flex items-center justify-between">
            {COUNTRY_LABEL[c]} <ChevronDown size={16} className="text-white/40" />
          </summary>
          <ul className="mt-4 space-y-3">
            {(byCountry[c] || []).map(s => (
              <li key={`${s.indicator_key}-${s.reference_period}`}
                  className="border-l-2 border-[#D4AF37]/40 pl-3 text-sm">
                <div className="text-white/80">{s.label} · {s.reference_period} · {STATUS_LABEL[s.publication_status]}</div>
                <div className="text-white/50 text-xs mt-1">{s.source_agency} · veröff. {s.publication_date}</div>
                <div className="text-white/45 text-xs mt-1 leading-relaxed">{s.definition}</div>
                <a href={s.source_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline mt-1">
                  Amtliche Quelle öffnen <ExternalLink size={11} />
                </a>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

export default function SocietyBarometerPage() {
  const loc = useLocation();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [stats, setStats] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const params = new URLSearchParams(loc.search);
  const country = params.get("land") || "DACH";
  const topic = params.get("thema") || "";

  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const [ov, all, srcs] = await Promise.all([
          publicStatisticsOverview(), publicStatistics({}), publicStatisticsSources(),
        ]);
        setOverview(ov); setStats(all.items || []); setSources(srcs.sources || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || String(e));
      } finally { setLoading(false); }
    })();
  }, []);

  // JSON-LD injizieren (CollectionPage + Dataset je Kennzahl)
  useEffect(() => {
    if (!stats.length) return;
    const { collection, datasets } = buildStructuredData(stats, sources);
    const nodes = [];
    const inject = (obj, id) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.barometerLd = id;
      el.text = JSON.stringify(obj);
      document.head.appendChild(el);
      nodes.push(el);
    };
    // Bestehende Barometer-LD entfernen (Idempotenz bei Rerender)
    document.querySelectorAll("script[data-barometer-ld]").forEach(n => n.remove());
    inject(collection, "collection");
    datasets.forEach((d, i) => inject(d, `dataset-${i}`));
    return () => nodes.forEach(n => n.remove());
  }, [stats, sources]);

  const filtered = useMemo(() => {
    return stats.filter(s => {
      if (country !== "DACH" && s.country !== country) return false;
      if (topic && s.topic !== topic) return false;
      return true;
    });
  }, [stats, country, topic]);

  function setParam(k, v) {
    const p = new URLSearchParams(loc.search);
    if (v) p.set(k, v); else p.delete(k);
    nav({ pathname: loc.pathname, search: p.toString() });
  }

  const oneP = overview?.ranges?.find(r => r.indicator_key === "one_person_households_share");
  const tfr = overview?.ranges?.find(r => r.indicator_key === "total_fertility_rate");
  const mAgg = overview?.aggregates?.find(a => a.indicator_key === "marriages_total");
  const dAgg = overview?.aggregates?.find(a => a.indicator_key === "divorces_total");
  const bfsChild = stats.find(s => s.indicator_key === "relationship_quality_child_decision_share");

  return (
    <div className="min-h-screen bg-[#060810] text-[#F5EFE7] pt-24" data-testid="society-barometer">
      <SEO
        title="DACH-Beziehungsstatistiken | SAT Gesellschaftsbarometer"
        description="Aktuelle amtliche Zahlen zu Ehe, Scheidung, Einpersonenhaushalten und Familie in Deutschland, Österreich und der Schweiz – transparent eingeordnet von SAT."
        canonicalPath="/gesellschaftsbarometer"
        path="/gesellschaftsbarometer"
      />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]/80 mb-3">
          SAT Gesellschaftsbarometer · DACH
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-tight mb-4">
          Beziehungen verändern sich. Bewusstsein verändert, wie wir damit umgehen.
        </h1>
        <p className="text-white/70 max-w-3xl leading-relaxed">
          Das SAT Gesellschaftsbarometer bündelt aktuelle amtliche Daten aus
          Deutschland, Österreich und der Schweiz. Es zeigt Entwicklungen rund
          um Ehe, Trennung, Haushaltsformen und Familie – sachlich, transparent
          und ohne Menschen oder Lebensmodelle zu bewerten.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="#zahlen" className="px-5 py-3 bg-[#D4AF37] text-black rounded text-sm hover:bg-[#c19f2a]"
             data-testid="hero-cta-zahlen">Zahlen entdecken</a>
          <Link to="/sat-master" className="px-5 py-3 border border-white/25 rounded text-sm hover:bg-white/5">
            Die Spielregeln des Lebens kennenlernen
          </Link>
        </div>
        <div className="mt-6 text-xs text-white/40 space-y-1">
          <div>Datenstand: 18. August 2026</div>
          <div>Quellen: Destatis · Statistik Austria · Bundesamt für Statistik BFS</div>
          <div>Datenjahre und Erhebungsmethoden können je Kennzahl abweichen.</div>
        </div>
      </section>

      {/* DACH auf einen Blick */}
      <section className="max-w-5xl mx-auto px-6 pb-12" id="zahlen">
        <h2 className="text-2xl font-serif mb-5">DACH auf einen Blick</h2>
        {loading && <div className="text-white/50 text-sm">Lade …</div>}
        {err && <div className="text-rose-300 text-sm">{err}</div>}
        {overview && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {mAgg && (
              <div className="border border-white/10 rounded-lg p-5" data-testid="agg-marriages">
                <div className="text-3xl font-serif">{fmt(mAgg.value_numeric, "count")}</div>
                <div className="text-sm text-white/70 mt-1">Eheschließungen</div>
                <div className="text-xs text-white/40 mt-2">Summe DE + AT + CH · {mAgg.reference_period}</div>
              </div>
            )}
            {dAgg && (
              <div className="border border-white/10 rounded-lg p-5" data-testid="agg-divorces">
                <div className="text-3xl font-serif">{fmt(dAgg.value_numeric, "count")}</div>
                <div className="text-sm text-white/70 mt-1">Scheidungen</div>
                <div className="text-xs text-white/40 mt-2">Summe DE + AT + CH · {dAgg.reference_period}</div>
              </div>
            )}
            {oneP && (
              <div className="border border-white/10 rounded-lg p-5" data-testid="range-onep">
                <div className="text-3xl font-serif">{fmt(oneP.min, "percent")}–{fmt(oneP.max, "percent")}</div>
                <div className="text-sm text-white/70 mt-1">Einpersonenhaushalte</div>
                <div className="text-xs text-white/40 mt-2">Spannweite · {oneP.years.join("/")}</div>
              </div>
            )}
            {tfr && (
              <div className="border border-white/10 rounded-lg p-5" data-testid="range-tfr">
                <div className="text-3xl font-serif">{fmt(tfr.min, "children_per_woman")}–{fmt(tfr.max, "children_per_woman")}</div>
                <div className="text-sm text-white/70 mt-1">Kinder je Frau</div>
                <div className="text-xs text-white/40 mt-2">Zusammengefasste Geburtenziffer · {tfr.years.join("/")}</div>
              </div>
            )}
          </div>
        )}
        <p className="mt-5 text-sm text-white/55 leading-relaxed max-w-3xl">
          Die Summen der Eheschließungen und Scheidungen sind voneinander
          unabhängige Ereigniszahlen. Ihr Verhältnis ist keine Scheidungsrate.
          Die Spannweiten dienen der Orientierung; nationale Definitionen und
          Methoden sind in den Quellenhinweisen erläutert.
        </p>
      </section>

      {/* Länderauswahl */}
      <section className="max-w-5xl mx-auto px-6 pb-6" aria-labelledby="land-tabs">
        <h2 id="land-tabs" className="sr-only">Länderauswahl</h2>
        <div className="flex flex-wrap gap-2" role="tablist" data-testid="country-tabs">
          {["DACH", "DE", "AT", "CH"].map(c => (
            <button key={c} role="tab" aria-selected={country === c}
                    onClick={() => setParam("land", c === "DACH" ? "" : c)}
                    data-testid={`country-tab-${c}`}
                    className={`px-4 py-2 rounded text-sm border ${
                      country === c
                        ? "bg-[#D4AF37]/15 border-[#D4AF37]/50 text-[#D4AF37]"
                        : "border-white/15 text-white/70 hover:border-white/30"
                    }`}>
              {c === "DACH" ? "DACH-Überblick" : COUNTRY_LABEL[c]}
            </button>
          ))}
        </div>

        {/* Themenfilter */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="topic-filters">
          <button onClick={() => setParam("thema", "")}
                  data-testid="topic-filter-alle"
                  className={`px-3 py-1.5 rounded text-xs border ${
                    !topic ? "bg-white/10 border-white/30 text-white" : "border-white/15 text-white/60 hover:border-white/25"
                  }`}>Alle Themen</button>
          {Object.entries(TOPIC_LABEL).map(([k, label]) => (
            <button key={k} onClick={() => setParam("thema", k)}
                    data-testid={`topic-filter-${k}`}
                    className={`px-3 py-1.5 rounded text-xs border ${
                      topic === k ? "bg-white/10 border-white/30 text-white" : "border-white/15 text-white/60 hover:border-white/25"
                    }`}>{label}</button>
          ))}
        </div>
      </section>

      {/* Karten */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        {!loading && filtered.length === 0 && (
          <div className="text-white/50 text-sm border border-white/10 rounded p-6" data-testid="empty-state">
            Für diese Auswahl liegt noch keine vergleichbare amtliche Zahl vor.
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map(s => <StatisticCard key={s.id} s={s} />)}
        </div>
      </section>

      {/* Vergleichs-Diagramme */}
      {overview && country === "DACH" && (
        <section className="max-w-5xl mx-auto px-6 pb-12">
          <h2 className="text-2xl font-serif mb-5">Vergleiche mit gleicher Methodik</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {oneP && (
              <BarChart
                title="Anteil Einpersonenhaushalte (in %)"
                unit="percent_of_private_households"
                ariaId="chart-onep"
                data={oneP.countries.map(c => ({ country: c.country, value: c.value, reference_period: c.reference_period }))}
              />
            )}
            {tfr && (
              <BarChart
                title="Zusammengefasste Geburtenziffer (Kinder je Frau)"
                unit="children_per_woman"
                ariaId="chart-tfr"
                data={tfr.countries.map(c => ({ country: c.country, value: c.value, reference_period: c.reference_period }))}
              />
            )}
          </div>
          <p className="mt-4 text-xs text-white/45">
            Absolute Ereigniszahlen werden nicht als Länderranking dargestellt.
            Nationale Scheidungsziffern sind Modellkennzahlen mit
            unterschiedlichen Definitionen und erscheinen nur in den Länderdetails.
          </p>
        </section>
      )}

      {/* Was Zahlen zeigen — und was nicht */}
      <section className="max-w-4xl mx-auto px-6 pb-14">
        <h2 className="text-2xl font-serif mb-4">Zahlen schaffen Orientierung. Nicht Verurteilung.</h2>
        <p className="text-white/70 leading-relaxed mb-5">
          Statistiken machen Entwicklungen sichtbar, aber sie erzählen nicht die
          Geschichte eines einzelnen Menschen. Ein Einpersonenhaushalt kann
          Ausdruck von Freiheit, Übergang, Verlust, räumlicher Organisation oder
          vielen anderen Lebenssituationen sein. Eine Scheidung kann schmerzhaft
          sein, zugleich aber auch eine bewusste Grenze oder einen notwendigen
          Neubeginn markieren. Deshalb verbindet SAT Fakten mit einer
          respektvollen, differenzierten Betrachtung.
        </p>
        <ul className="grid sm:grid-cols-2 gap-3 text-sm text-white/70">
          <li className="border-l-2 border-[#D4AF37]/50 pl-3">Lebensform ist nicht gleich Lebensqualität.</li>
          <li className="border-l-2 border-[#D4AF37]/50 pl-3">Bestand ist nicht gleich Verbundenheit.</li>
          <li className="border-l-2 border-[#D4AF37]/50 pl-3">Trennung ist nicht automatisch Scheitern.</li>
          <li className="border-l-2 border-[#D4AF37]/50 pl-3">Statistik ist Ausgangspunkt, nicht Urteil.</li>
        </ul>
      </section>

      {/* SAT-Brücke (Schweizer Vertiefung) */}
      {bfsChild && (
        <section className="max-w-4xl mx-auto px-6 pb-14">
          <div className="border border-[#D4AF37]/30 rounded-lg p-6 bg-white/[0.02]" data-testid="bfs-relationship-card">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mb-2">Schweizer Vertiefung</div>
            <div className="text-4xl font-serif mb-2">63 % · Beziehung zählt</div>
            <p className="text-white/70 text-sm mb-3">
              Für 63 % der 20- bis 39-Jährigen beeinflusst die Qualität der
              Paarbeziehung den Entscheid für oder gegen ein weiteres Kind stark
              oder sehr stark.
            </p>
            <p className="text-white/45 text-xs mb-3">
              Schweizer Erhebung zu Familien und Generationen 2023,
              veröffentlicht durch das BFS.
            </p>
            <p className="text-white/45 text-xs">
              Die Befragung zeigt die wahrgenommene Bedeutung der Paarbeziehung
              für diese Entscheidung. Sie beweist weder eine Ursache für sinkende
              Geburtenzahlen noch einen DACH-weiten Wert.
            </p>
            <a href={bfsChild.source_url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline mt-3">
              BFS-Quelle öffnen <ExternalLink size={11} />
            </a>
          </div>
        </section>
      )}

      {/* SAT-Einordnung */}
      <section className="max-w-4xl mx-auto px-6 pb-14">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mb-2">Wo SAT ansetzt</div>
        <h2 className="text-3xl font-serif mb-4">Beziehungskompetenz ist kein Zufallsprodukt.</h2>
        <p className="text-white/70 leading-relaxed mb-6">
          Wir lernen Lesen, Rechnen und einen Beruf. Nur selten lernen wir
          systematisch, Emotionen zu regulieren, Muster zu erkennen, Bedürfnisse
          klar auszudrücken, Grenzen zu achten, Verantwortung zu übernehmen und
          Veränderung bewusst zu gestalten. Die Spielregeln des Lebens geben
          diesen Fähigkeiten einen zusammenhängenden Rahmen.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/sat-master" className="px-5 py-3 bg-[#D4AF37] text-black rounded text-sm hover:bg-[#c19f2a]"
                data-testid="cta-sat-master">
            SAT MASTER entdecken
          </Link>
          <Link to="/erstgespraech" className="px-5 py-3 border border-white/25 rounded text-sm hover:bg-white/5"
                data-testid="cta-erstgespraech">
            Erstgespräch buchen
          </Link>
        </div>
        <p className="mt-6 text-xs text-white/40 max-w-3xl leading-relaxed border-l-2 border-white/15 pl-3">
          Zahlen beschreiben gesellschaftliche Entwicklungen. Sie erklären weder
          individuelle Lebensgeschichten noch beweisen sie eine bestimmte
          Ursache. Für SAT sind sie der Ausgangspunkt für eine tiefere Frage:
          Welche Fähigkeiten brauchen Menschen, um sich selbst, ihre Beziehungen
          und ihre Lebensentscheidungen bewusster zu gestalten?
        </p>
      </section>

      {/* Quellen & Methodik */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <h2 className="text-2xl font-serif mb-5">Quellen und Methodik</h2>
        <SourcesAccordion sources={sources} />
      </section>
    </div>
  );
}
